import { Box, Skeleton } from '@mui/material';
import Stack from '@mui/material/Stack';
import type { InvoicesOverviewsSummary } from '@vy/api-definition';
import { ClockWatch, CurrencyDollarMediumCircle, DocSheet, PendingArrowCircle } from '@vy/ui-design-system';
import { isNotNullOrUndefined, newUUID } from '@vy/utils';
import React, { Suspense, useMemo } from 'react';
import { Price } from '../../../../components/design/Price';
import { useGetInvoicesSummary } from '../../../../hooks/requests/use-get-invoices-summary';
import { SummaryCard } from '../../../../components/new-design/data-display/SummaryCard/SummaryCard';
import { InvoiceBillingStatus } from '@vy/types';

type OptionCard = {
  id: string;
  filterKey?: InvoiceBillingStatus;
  label: string;
  total?: number;
  count?: number;
  icon?: React.ReactNode;
  iconBackground?: string;
};

type InvoicesPageInfoCardsProps = {
  invoicesOverviewsSummary?: InvoicesOverviewsSummary;
  onCardClick?: (id: string, filterKey?: InvoiceBillingStatus) => void;
  activeBillingStatusFilters?: InvoiceBillingStatus[];
};

export const InvoicesPageInfoCards: React.FC<InvoicesPageInfoCardsProps> = ({
  invoicesOverviewsSummary,
  onCardClick,
  activeBillingStatusFilters,
}) => {
  const { data } = useGetInvoicesSummary({ suspense: true });
  const totalAmount = invoicesOverviewsSummary?.totalAmount;
  const openAmount = invoicesOverviewsSummary?.openAmount;
  const overdueAmount = invoicesOverviewsSummary?.overdueAmount;
  const pendingPaymentAmount = invoicesOverviewsSummary?.pendingPaymentAmount;
  const paidAmount = invoicesOverviewsSummary?.paidAmount;

  const options: OptionCard[] = useMemo(
    () => [
      { id: newUUID(), label: 'All invoices', total: totalAmount },
      {
        id: newUUID(),
        filterKey: 'None',
        label: 'Open',
        total: openAmount,
        count: data?.None ?? 0,
        iconBackground: '#E7F2FF',
        icon: <DocSheet />,
      },
      {
        id: newUUID(),
        filterKey: 'Overdue',
        label: 'Overdue',
        total: overdueAmount,
        count: data?.Overdue ?? 0,
        iconBackground: '#FEEBEE',
        icon: <ClockWatch />,
      },
      {
        id: newUUID(),
        filterKey: 'PendingPayment',
        label: 'Pending payment',
        total: pendingPaymentAmount,
        count: data?.PendingPayment ?? 0,
        iconBackground: '#FDF1D7',
        icon: <PendingArrowCircle />,
      },
      {
        id: newUUID(),
        filterKey: 'Paid',
        label: 'Paid',
        total: paidAmount,
        count: data?.Paid ?? 0,
        iconBackground: '#DFFAE5',
        icon: <CurrencyDollarMediumCircle />,
      },
    ],
    [data, totalAmount, openAmount, overdueAmount, pendingPaymentAmount, paidAmount],
  );

  return (
    <Suspense fallback={<Skeleton variant="rectangular" height={40} />}>
      <Stack
        direction="row"
        sx={{
          gap: 1,
          alignItems: "center",
          width: "100%",
          flexWrap: "wrap"
        }}>
        {options.map(({ id, label, total, icon, count, iconBackground, filterKey }) => {
          const titleWithCount = `${count ?? ''} ${label}`;

          return (
            <Box
              key={id}
              sx={{
                flex: "1 1 0",
                minWidth: "220px"
              }}>
              <SummaryCard
                title={titleWithCount}
                value={
                  isNotNullOrUndefined(total) ? (
                    <Price rounded variant="body2">
                      {total}
                    </Price>
                  ) : undefined
                }
                icon={icon}
                iconBackground={iconBackground}
                isActive={
                  isNotNullOrUndefined(filterKey)
                    ? (activeBillingStatusFilters?.includes(filterKey) ?? false)
                    : false
                }
                onClick={
                  isNotNullOrUndefined(onCardClick)
                    ? () => {
                        onCardClick(id, filterKey);
                      }
                    : undefined
                }
              />
            </Box>
          );
        })}
      </Stack>
    </Suspense>
  );
};
